<template>
  <v-container fluid class="content-container">
    <v-row class="mb-2" align="center">
      <v-col cols="12" md="6">
        <h2>Onboarding</h2>
      </v-col>
      <v-col cols="12" md="6" class="text-md-right">
        <v-btn icon @click="refreshAll"><v-icon>mdi-refresh</v-icon></v-btn>
      </v-col>
    </v-row>

    <v-card class="data-card">
      <v-card-title class="card-header">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          placeholder="Rechercher (business, propriétaire, téléphone)..."
          single-line
          hide-details
          outlined
          dense
          clearable
          class="table-search"
        ></v-text-field>

        <div class="status-filters">
          <v-chip
            small
            :outlined="volet !== 'A_APPELER'"
            color="blue-grey"
            :class="['filter-chip', volet === 'A_APPELER' ? 'white--text' : '']"
            @click="setVolet('A_APPELER')"
          >
            <v-icon left x-small>mdi-phone-alert</v-icon>
            À appeler{{ countLabel('A_APPELER') }}
          </v-chip>
          <v-chip
            small
            :outlined="volet !== 'EN_COURS'"
            color="info"
            :class="['filter-chip', volet === 'EN_COURS' ? 'white--text' : '']"
            @click="setVolet('EN_COURS')"
          >
            <v-icon left x-small>mdi-phone-check</v-icon>
            En cours{{ countLabel('EN_COURS') }}
          </v-chip>
          <v-chip
            small
            :outlined="volet !== 'TERMINES'"
            color="success"
            :class="['filter-chip', volet === 'TERMINES' ? 'white--text' : '']"
            @click="setVolet('TERMINES')"
          >
            <v-icon left x-small>mdi-check-circle</v-icon>
            Terminés{{ countLabel('TERMINES') }}
          </v-chip>
          <v-chip
            small
            :outlined="volet !== 'A_RAPPELER'"
            color="orange"
            :class="['filter-chip', volet === 'A_RAPPELER' ? 'white--text' : '']"
            @click="setVolet('A_RAPPELER')"
          >
            <v-icon left x-small>mdi-bell-ring-outline</v-icon>
            À rappeler{{ countLabel('A_RAPPELER') }}
          </v-chip>
        </div>

        <v-spacer></v-spacer>
        <v-chip class="results-chip ml-3" small>
          {{ leads.length }} résultat(s)
        </v-chip>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="leads"
        :loading="loading"
        :items-per-page="25"
        :item-class="rowClass"
        loading-text="Chargement..."
        no-data-text="Aucune demande dans ce volet."
        class="modern-table"
      >
        <template v-slot:[`item.business_name`]="{ item }">
          <div class="d-flex align-center">
            <strong>{{ item.business_name }}</strong>
            <v-tooltip v-if="item.notes" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-note-text-outline</v-icon>
              </template>
              <span>{{ item.notes }}</span>
            </v-tooltip>
            <v-tooltip v-if="item.callback_at && volet !== 'A_RAPPELER'" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small class="ml-1" :color="isCallbackDue(item) ? 'warning' : 'grey'" v-bind="attrs" v-on="on">mdi-calendar-clock</v-icon>
              </template>
              <span>Rappel le {{ formatDateOnly(item.callback_at) }}</span>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:[`item.phones`]="{ item }">
          <div>{{ item.phone1 }}</div>
          <div v-if="item.phone2" class="grey--text caption">{{ item.phone2 }}</div>
        </template>

        <template v-slot:[`item.referrer`]="{ item }">
          <div v-if="item.referrer">
            <strong>{{ item.referrer.first_name }} {{ item.referrer.last_name }}</strong>
            <div class="grey--text caption">{{ item.referrer.phone }} · {{ item.referrer.code }}</div>
          </div>
          <span v-else>—</span>
        </template>

        <template v-slot:[`item.linked_business`]="{ item }">
          <span v-if="item.business">{{ item.business.name }}</span>
          <span v-else class="grey--text">—</span>
        </template>

        <template v-slot:[`item.reward_state`]="{ item }">
          <v-chip x-small :color="item.status === 'CONVERTI' ? 'success' : 'grey'" text-color="white">
            {{ item.status === 'CONVERTI' ? 'RÉCOMPENSÉE' : 'EN ATTENTE 1ER PAIEMENT' }}
          </v-chip>
        </template>

        <template v-slot:[`item.callback`]="{ item }">
          <v-chip
            v-if="item.callback_at"
            x-small
            :color="isCallbackDue(item) ? 'warning' : 'grey'"
            :text-color="isCallbackDue(item) ? 'black' : 'white'"
          >
            {{ isCallbackDue(item) ? 'RAPPEL DÛ' : `RAPPEL LE ${formatDateOnly(item.callback_at)}` }}
          </v-chip>
          <span v-else class="grey--text">—</span>
        </template>

        <template v-slot:[`item.created_at`]="{ item }">
          {{ formatDate(item.created_at) }}
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <v-chip x-small :color="statusColor(item.status)" text-color="white">
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex flex-wrap" style="gap: 6px;">
            <v-btn x-small outlined color="grey darken-1" @click="openNotes(item)">
              <v-icon left x-small>mdi-note-edit-outline</v-icon>
              Notes
            </v-btn>
            <v-btn
              v-if="showContactedAction(item)"
              x-small outlined color="info"
              :loading="contactingId === item.id"
              @click="markContacted(item)"
            >
              <v-icon left x-small>mdi-phone-check</v-icon>
              Contacté
            </v-btn>
            <v-btn
              v-if="showRejectAction(item)"
              x-small outlined color="error"
              @click="openReject(item)"
            >
              <v-icon left x-small>mdi-close</v-icon>
              Rejeter
            </v-btn>
            <v-btn
              v-if="showAssociateAction(item)"
              x-small color="primary" dark
              @click="openAssociate(item)"
            >
              <v-icon left x-small>mdi-link-variant</v-icon>
              Associer
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Notes / rappel -->
    <v-dialog v-model="notesDialog" max-width="480">
      <v-card v-if="notesLead">
        <v-card-title>Notes</v-card-title>
        <v-card-text>
          <p>
            <strong>{{ notesLead.business_name }}</strong> — {{ notesLead.owner_name }}
          </p>
          <v-textarea
            v-model="notesText"
            label="Note (optionnel)"
            outlined dense rows="3"
          />
          <v-menu
            v-model="notesCallbackMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="notesCallbackAt"
                label="Rappeler le (optionnel)"
                prepend-inner-icon="mdi-calendar-clock"
                readonly
                outlined dense hide-details clearable
                class="mb-2"
                v-bind="attrs"
                v-on="on"
                @click:clear="notesCallbackAt = null"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="notesCallbackAt"
              no-title
              @input="notesCallbackMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="notesDialog = false">Annuler</v-btn>
          <v-btn color="primary" :loading="savingNotes" @click="confirmNotes">Enregistrer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Rejeter -->
    <v-dialog v-model="rejectDialog" max-width="440">
      <v-card v-if="rejectLead">
        <v-card-title>Rejeter la demande</v-card-title>
        <v-card-text>
          <p>
            <strong>{{ rejectLead.business_name }}</strong> — {{ rejectLead.owner_name }}
          </p>
          <v-textarea
            v-model="rejectNote"
            label="Note (optionnel)"
            outlined dense rows="3"
          />
          <v-menu
            v-model="rejectCallbackMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="rejectCallbackAt"
                label="Rappeler le (optionnel)"
                prepend-inner-icon="mdi-calendar-clock"
                readonly
                outlined dense hide-details clearable
                v-bind="attrs"
                v-on="on"
                @click:clear="rejectCallbackAt = null"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="rejectCallbackAt"
              no-title
              @input="rejectCallbackMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="rejectDialog = false">Annuler</v-btn>
          <v-btn color="error" :loading="rejecting" @click="confirmReject">Rejeter</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Associer -->
    <v-dialog v-model="associateDialog" max-width="480">
      <v-card v-if="associateLead">
        <v-card-title>Associer un business</v-card-title>
        <v-card-text>
          <p class="mb-3">
            Demande : <strong>{{ associateLead.business_name }}</strong> — {{ associateLead.owner_name }}<br>
            Parrain : <span v-if="associateLead.referrer">
              {{ associateLead.referrer.first_name }} {{ associateLead.referrer.last_name }}
              ({{ associateLead.referrer.code }})
            </span>
          </p>
          <v-autocomplete
            v-model="selectedBusinessId"
            :items="availableBusinesses"
            :loading="businessesLoading"
            item-text="name"
            item-value="id"
            label="Rechercher le business créé…"
            dense outlined hide-details clearable
          >
            <template v-slot:item="{ item }">
              {{ item.name }} <span class="grey--text ml-1">({{ item.phone }})</span>
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="associateDialog = false">Annuler</v-btn>
          <v-btn
            color="primary"
            :disabled="!selectedBusinessId"
            :loading="associating"
            @click="confirmAssociate"
          >
            Associer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script>
import moment from 'moment'

const VOLET_PARAMS = {
  A_APPELER: { status: 'NOUVEAU', linked: 0 },
  EN_COURS: { status: 'CONTACTE', linked: 0 },
  TERMINES: { linked: 1 },
  A_RAPPELER: { callback: 1 },
}

// callback_at arrive en ISO minuit UTC : un parse moment() classique dans un
// navigateur UTC-5 (Haïti) recule d'un jour. On parse donc la date seule.
const parseDateOnly = v => moment(String(v).substring(0, 10), 'YYYY-MM-DD')

export default {
  name: 'OnboardingView',
  data: () => ({
    loading: false,
    leads: [],
    search: '',
    volet: 'A_APPELER',
    counts: { A_APPELER: null, EN_COURS: null, TERMINES: null, A_RAPPELER: null },
    searchTimeout: null,

    allBusinesses: [],
    businessesLoading: false,

    associateDialog: false,
    associateLead: null,
    selectedBusinessId: null,
    associating: false,

    rejectDialog: false,
    rejectLead: null,
    rejectNote: '',
    rejectCallbackAt: null,
    rejectCallbackMenu: false,
    rejecting: false,

    notesDialog: false,
    notesLead: null,
    notesText: '',
    notesCallbackAt: null,
    notesCallbackMenu: false,
    savingNotes: false,

    contactingId: null,

    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
  }),
  computed: {
    headers() {
      const cols = [
        { text: 'Business', value: 'business_name' },
        { text: 'Propriétaire', value: 'owner_name' },
        { text: 'Téléphones', value: 'phones', sortable: false },
        { text: 'Parrain', value: 'referrer', sortable: false },
      ]
      if (this.volet === 'TERMINES') {
        cols.push({ text: 'Business lié', value: 'linked_business', sortable: false })
        cols.push({ text: 'Récompense', value: 'reward_state', sortable: false })
      }
      if (this.volet === 'A_RAPPELER') {
        cols.push({ text: 'Rappel', value: 'callback', sortable: false })
      }
      cols.push({ text: 'Date', value: 'created_at' })
      cols.push({ text: 'Statut', value: 'status' })
      cols.push({ text: 'Actions', value: 'actions', sortable: false })
      return cols
    },
    availableBusinesses() {
      return this.allBusinesses.filter(b => !b.referrer_id)
    },
  },
  watch: {
    search() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => this.load(), 300)
    },
  },
  mounted() {
    this.load()
    this.loadCounts()
  },
  methods: {
    setVolet(value) {
      if (this.volet === value) return
      this.volet = value
      this.load()
    },
    countLabel(volet) {
      const n = this.counts[volet]
      return n === null || n === undefined ? '' : ` (${n})`
    },
    async refreshAll() {
      await Promise.all([this.load(), this.loadCounts()])
    },
    async load() {
      this.loading = true
      try {
        const res = await this.$axios.get('/referral-leads', {
          params: {
            ...VOLET_PARAMS[this.volet],
            search: this.search || undefined,
          },
        })
        this.leads = res.data.data
        this.$set(this.counts, this.volet, this.leads.length)
      } catch (e) {
        this.notify('Erreur lors du chargement des demandes', 'error')
      } finally {
        this.loading = false
      }
    },
    async loadCounts() {
      try {
        const volets = Object.keys(VOLET_PARAMS)
        const results = await Promise.all(
          volets.map(v => this.$axios.get('/referral-leads', { params: VOLET_PARAMS[v] }))
        )
        volets.forEach((v, i) => this.$set(this.counts, v, results[i].data.data.length))
      } catch (e) {
        // Les compteurs sont secondaires : on ignore silencieusement les erreurs.
      }
    },
    async loadBusinesses() {
      this.businessesLoading = true
      try {
        const res = await this.$axios.get('/businesses?orderasc=name')
        this.allBusinesses = res.data
      } catch (e) {
        this.notify('Erreur lors du chargement des businesses', 'error')
      } finally {
        this.businessesLoading = false
      }
    },
    showContactedAction(item) {
      if (this.volet === 'A_APPELER') return true
      if (this.volet === 'A_RAPPELER') return item.status === 'NOUVEAU'
      return false
    },
    showRejectAction() {
      return this.volet === 'A_APPELER' || this.volet === 'EN_COURS'
    },
    showAssociateAction(item) {
      if (this.volet === 'TERMINES') return false
      if (this.volet === 'A_RAPPELER') return !item.business_id
      return true
    },
    async markContacted(lead) {
      this.contactingId = lead.id
      try {
        await this.$axios.put(`/referral-leads/${lead.id}`, { status: 'CONTACTE' })
        this.notify('Lead marqué contacté', 'success')
        await this.refreshAll()
      } catch (e) {
        this.notify('Erreur : ' + (e.response?.data?.message || 'échec de la mise à jour'), 'error')
      } finally {
        this.contactingId = null
      }
    },
    openReject(lead) {
      this.rejectLead = lead
      this.rejectNote = ''
      this.rejectCallbackAt = null
      this.rejectDialog = true
    },
    async confirmReject() {
      if (!this.rejectLead) return
      this.rejecting = true
      try {
        await this.$axios.put(`/referral-leads/${this.rejectLead.id}`, {
          status: 'REJETE',
          notes: this.rejectNote || undefined,
          callback_at: this.rejectCallbackAt || undefined,
        })
        this.rejectDialog = false
        this.notify('Demande rejetée', 'success')
        await this.refreshAll()
      } catch (e) {
        this.notify('Erreur : ' + (e.response?.data?.message || 'échec du rejet'), 'error')
      } finally {
        this.rejecting = false
      }
    },
    openNotes(lead) {
      this.notesLead = lead
      this.notesText = lead.notes || ''
      this.notesCallbackAt = lead.callback_at ? parseDateOnly(lead.callback_at).format('YYYY-MM-DD') : null
      this.notesDialog = true
    },
    async confirmNotes() {
      if (!this.notesLead) return
      this.savingNotes = true
      try {
        await this.$axios.put(`/referral-leads/${this.notesLead.id}`, {
          notes: this.notesText || null,
          callback_at: this.notesCallbackAt || null,
        })
        this.notesDialog = false
        this.notify('Notes enregistrées', 'success')
        await this.refreshAll()
      } catch (e) {
        this.notify('Erreur : ' + (e.response?.data?.message || 'échec de l\'enregistrement'), 'error')
      } finally {
        this.savingNotes = false
      }
    },
    async openAssociate(lead) {
      this.associateLead = lead
      this.selectedBusinessId = null
      this.associateDialog = true
      if (!this.allBusinesses.length) await this.loadBusinesses()
    },
    async confirmAssociate() {
      if (!this.selectedBusinessId || !this.associateLead) return
      this.associating = true
      try {
        await this.$axios.post(`/businesses/${this.selectedBusinessId}/referrer`, {
          code: this.associateLead.referrer && this.associateLead.referrer.code,
          lead_id: this.associateLead.id,
        })
        this.associateDialog = false
        this.notify('Business associé, demande traitée', 'success')
        this.loadBusinesses()
        await this.refreshAll()
      } catch (e) {
        this.notify('Erreur : ' + (e.response?.data?.message || "échec de l'association"), 'error')
      } finally {
        this.associating = false
      }
    },
    isCallbackDue(item) {
      return !!item.callback_at && parseDateOnly(item.callback_at).isSameOrBefore(moment(), 'day')
    },
    rowClass(item) {
      return this.volet === 'A_RAPPELER' && this.isCallbackDue(item) ? 'callback-due-row' : ''
    },
    notify(text, color) {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },
    statusColor(status) {
      return { NOUVEAU: 'blue-grey', CONTACTE: 'info', CONVERTI: 'success', REJETE: 'error' }[status] || 'grey'
    },
    statusLabel(status) {
      return { NOUVEAU: 'NOUVEAU', CONTACTE: 'CONTACTÉ', CONVERTI: 'CONVERTI', REJETE: 'REJETÉ' }[status] || status
    },
    formatDate(v) {
      return moment(v).format('DD/MM/YYYY HH:mm')
    },
    formatDateOnly(v) {
      return parseDateOnly(v).format('DD/MM/YYYY')
    },
  },
}
</script>

<style lang="scss" scoped>
.content-container {
  padding: 0 24px 24px;
}

.data-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.card-header {
  padding: 16px 20px !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
}

.theme--dark .card-header {
  border-bottom-color: rgba(255, 255, 255, 0.06);
}

.table-search {
  max-width: 320px;

  ::v-deep .v-input__slot {
    min-height: 40px !important;
    border-radius: 8px !important;
  }
}

.status-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.filter-chip {
  cursor: pointer;
  font-weight: 500 !important;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
}

.results-chip {
  font-weight: 500;
}

.modern-table {
  ::v-deep th {
    font-weight: 600 !important;
    font-size: 12px !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
  }

  ::v-deep td {
    padding: 16px !important;
  }

  ::v-deep tbody tr:hover {
    background: rgba(0, 63, 116, 0.04);
  }

  ::v-deep tbody tr.callback-due-row {
    background: rgba(246, 141, 58, 0.08);
  }

  ::v-deep tbody tr.callback-due-row:hover {
    background: rgba(246, 141, 58, 0.14);
  }
}

.theme--dark .modern-table ::v-deep tbody tr:hover {
  background: rgba(255, 255, 255, 0.04);
}

.theme--dark .modern-table ::v-deep tbody tr.callback-due-row {
  background: rgba(246, 141, 58, 0.16);
}
</style>
